import { computed, effect, Injectable, OnInit, signal, WritableSignal } from "@angular/core";
import { Perfil, Rol, TokenInterface } from "../interfaces/login.interface";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { environment } from "../../../../environments/environment.development";
import CONSTANTS from "src/utils/resources/constants";

const apiVucUrl = environment.apiVucUrl

@Injectable({
    providedIn: 'root'
})
export class LoginServices {
    public tipoDocumento: WritableSignal<string> = signal('')
    /**
     * Session actual del usuario autenticado. Aca se almacena toda la info del perfil de usuario.
     */
    private _session: WritableSignal<TokenInterface | null> = signal(null);
    private _role: Rol | null = null;
    private _sessionToken = computed(() => this._session()?.token)
    private consoleEnabled: WritableSignal<boolean> = signal(CONSTANTS.consoleEnabled);

    constructor(private http: HttpClient) {}

    login(user: string, password: string) {
        return this.http.post<TokenInterface>(apiVucUrl + `api/v1.0/auth/token`, {
            'usuario': user,
            'clave': password
        }, { observe: 'response' })
    }

    logout() {
        return this.http.post(apiVucUrl + `api/v1.0/auth/logout`, {
            'uniqueId': this.session()?.perfil.uniqueId
        }, { observe: 'response' })
    }

    removerSession() {
        sessionStorage.removeItem("userSession")
        this._session.set(null)
    }

    hasRole(specificRole: string): boolean {
        const currentSession = this.session();
        if (!currentSession || !currentSession.perfil?.roles) {
            return false;
        }
        return currentSession.perfil?.roles.some(role => role.rol === specificRole);
    }

    hasRoles(roles: string[]): boolean {
        const currentSession = this.session();
        if (!currentSession || !currentSession.perfil?.roles) {
            return false;
        }
        // Using Set for O(1) average time complexity lookups
        const rolesToCheck = new Set(roles);
        //console.log("ROLES DEL USUARIO", currentSession.perfil?.roles);
        return currentSession.perfil?.roles.some(sessionRol => rolesToCheck.has(sessionRol.rol));
    }

    getDefaultRoute(perfil: Perfil): string | undefined {
        let defaultRoute: string | undefined = '/notfound';
        if (perfil && perfil.roles && perfil.roles.length > 0) {
            defaultRoute = perfil.roles[0].routerLink;
        }
        return defaultRoute;
    }

    getDefaultRole(): Rol | null {
        const currentSession = this.session();
        if (!currentSession || !currentSession.perfil?.roles) {
            return null;
        }
        if (currentSession.perfil && currentSession.perfil?.roles && currentSession.perfil?.roles.length > 0) {
            this._role = currentSession.perfil?.roles[0];
        }
        return this._role;
    }

    get isValid() {
       return typeof this.expireToken === 'number' && Math.floor(Date.now() / 1000) < this.expireToken;
    }

    get expireToken() {
        if (!this.token) return null;
        
        return JSON.parse(atob(this.token?.split('.')[1]))?.exp
    }

    get token() {
        return this._sessionToken() ?? JSON.parse(sessionStorage.getItem('userSession') ?? '{}')?.token
    }

    get session(): WritableSignal<TokenInterface | null> {
        return this._session;
    }

    set session(value: WritableSignal<TokenInterface | null>) {
        this._session = value;
    }

    /**
     * Sync only once on startup
     */
    loadSessionEffect = effect(() => {
        const userSession = sessionStorage.getItem('userSession');
        if (userSession) {
            this.session.set(JSON.parse(userSession));
        }
        console.log(`session status: ${userSession != null}`);
    }, { allowSignalWrites: true })
    /**
     * Sync current session when changes.
     */
    syncSessionEffect = effect(() => {
        sessionStorage.setItem("userSession", JSON.stringify(this.session()));
        console.log(`session status: ${this.session() != null}`);
    })
}
